<template>
    <div class="parts-table-wrapper">
        <div v-if="$slots['selected-row-actions']" class="table-actions">
            <slot name="selected-row-actions" :selectedRows="selectedRows" />
        </div>
        <table class="table table-striped table-class hidden-sm hidden-xs">
            <thead>
                <tr>
                    <th class="select-header">
                        <div class="form-check">
                            <input
                                class="form-check-input"
                                type="checkbox"
                                :checked="selectAll"
                                @change="toggleSelectAll"
                            />
                        </div>
                    </th>
                    <th
                        v-for="(column, idx) in columns"
                        :key="column.field || idx"
                        class="sortable-header"
                        @click="sortBy(column)"
                    >
                        <div class="sortable-header__content">
                            <span>{{ column.label }}</span>
                            <span v-if="sortField === column.field" class="sort-indicator">
                                <font-awesome-icon
                                    v-if="sortDirection !== 'asc'"
                                    :icon="['fas', 'chevron-up']"
                                />
                                <font-awesome-icon
                                    v-if="sortDirection !== 'desc'"
                                    :icon="['fas', 'chevron-down']"
                                />
                            </span>
                        </div>
                    </th>
                </tr>
            </thead>
            <tbody v-if="paginatedRows.length > 0">
                <tr v-for="(row, rowIndex) in paginatedRows" :key="rowKey(row, rowIndex)">
                    <td>
                        <div class="form-check">
                            <input
                                class="form-check-input"
                                type="checkbox"
                                :value="rowKey(row, rowIndex)"
                                :checked="isSelected(row, rowIndex)"
                                @change="toggleRowSelection(row, rowIndex)"
                            />
                        </div>
                    </td>
                    <td v-for="(column, colIndex) in columns" :key="colIndex">
                        {{ formatValue(row[column.field], column) }}
                    </td>
                </tr>
            </tbody>
            <tbody v-else>
                <tr>
                    <td colspan="100%" class="text-center" style="padding: 20px">
                        No data available
                    </td>
                </tr>
            </tbody>
        </table>

        <div v-if="enablePagination" class="d-flex justify-content-between align-items-center my-3">
            <div>
                <label for="itemsPerPage" class="me-2">Items per page:</label>
                <select
                    id="itemsPerPage"
                    v-model.number="itemsPerPage"
                    @change="handleItemsPerPageChange"
                    class="form-select d-inline-block w-auto"
                >
                    <option v-for="option in itemsPerPageOptions" :key="option" :value="option">
                        {{ option }}
                    </option>
                </select>
            </div>

            <div>
                <button
                    class="btn btn-danger"
                    @click="changePage(currentPage - 1)"
                    :disabled="currentPage === 1 || totalPages === 0"
                >
                    Previous
                </button>
                <span class="mx-3">
                    Page <strong>{{ Math.min(currentPage, Math.max(totalPages, 1)) }}</strong> of
                    <strong>{{ Math.max(totalPages, 1) }}</strong>
                </span>
                <button
                    class="btn btn-danger"
                    @click="changePage(currentPage + 1)"
                    :disabled="currentPage === totalPages || totalPages === 0"
                >
                    Next
                </button>
            </div>
        </div>
    </div>
</template>

<script>
    export default {
        name: 'PartsDashboardTable',
        props: {
            columns: {
                type: Array,
                required: true,
            },
            rows: {
                type: Array,
                default: () => [],
            },
            rowKeyField: {
                type: String,
                default: 'part_number',
            },
            itemsPerPageOptions: {
                type: Array,
                default: () => [10, 20, 50, 100],
            },
            enablePagination: {
                type: Boolean,
                default: true,
            },
        },
        data() {
            return {
                sortField: this.columns?.[0]?.field || '',
                sortDirection: 'asc',
                currentPage: 1,
                itemsPerPage: 10,
                selectedKeys: [],
            }
        },
        computed: {
            selectAll: {
                get() {
                    return (
                        this.rows.length > 0 &&
                        this.selectedKeys.length > 0 &&
                        this.selectedKeys.length === this.rows.length
                    )
                },
                set(value) {
                    if (value) {
                        this.selectedKeys = this.rows.map((row, index) => this.rowKey(row, index))
                    } else {
                        this.selectedKeys = []
                    }
                    this.emitSelection()
                },
            },
            sortedRows() {
                if (!this.sortField) {
                    return this.rows.slice()
                }

                return this.rows.slice().sort((a, b) => {
                    const column = this.columns.find((col) => col.field === this.sortField) || {}
                    const valueA = this.normalizeValue(a[this.sortField], column)
                    const valueB = this.normalizeValue(b[this.sortField], column)

                    if (valueA < valueB) {
                        return this.sortDirection === 'asc' ? -1 : 1
                    }
                    if (valueA > valueB) {
                        return this.sortDirection === 'asc' ? 1 : -1
                    }
                    return 0
                })
            },
            paginatedRows() {
                if (!this.enablePagination) {
                    return this.sortedRows
                }
                const start = (this.currentPage - 1) * this.itemsPerPage
                const end = start + this.itemsPerPage
                return this.sortedRows.slice(start, end)
            },
            totalPages() {
                if (!this.enablePagination) {
                    return this.sortedRows.length > 0 ? 1 : 0
                }
                return Math.ceil(this.sortedRows.length / this.itemsPerPage)
            },
            selectedRows() {
                return this.rows.filter((row, index) =>
                    this.selectedKeys.includes(this.rowKey(row, index))
                )
            },
        },
        watch: {
            rows: {
                handler(newRows) {
                    const validKeys = newRows.map((row, index) => this.rowKey(row, index))
                    this.selectedKeys = this.selectedKeys.filter((key) => validKeys.includes(key))
                    this.currentPage = 1
                },
                deep: true,
            },
            columns(newColumns) {
                if (!this.sortField && newColumns.length > 0) {
                    this.sortField = newColumns[0].field
                }
            },
        },
        methods: {
            rowKey(row, index) {
                // Prefer a stable key derived from the provided field, fall back to index only
                if (
                    this.rowKeyField &&
                    Object.prototype.hasOwnProperty.call(row, this.rowKeyField) &&
                    row[this.rowKeyField] !== undefined &&
                    row[this.rowKeyField] !== null
                ) {
                    return String(row[this.rowKeyField])
                }
                return String(index)
            },
            isSelected(row, index) {
                return this.selectedKeys.includes(this.rowKey(row, index))
            },
            toggleRowSelection(row, index) {
                const key = this.rowKey(row, index)
                if (this.isSelected(row, index)) {
                    this.selectedKeys = this.selectedKeys.filter((id) => id !== key)
                } else {
                    this.selectedKeys = [...this.selectedKeys, key]
                }
                this.emitSelection()
            },
            toggleSelectAll() {
                this.selectAll = !this.selectAll
            },
            emitSelection() {
                this.$emit('selection-changed', this.selectedRows)
            },
            sortBy(column) {
                if (this.sortField === column.field) {
                    this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
                } else {
                    this.sortField = column.field
                    this.sortDirection = 'asc'
                }
            },
            changePage(page) {
                if (page > 0 && page <= this.totalPages) {
                    this.currentPage = page
                }
            },
            handleItemsPerPageChange() {
                this.currentPage = 1
            },
            formatValue(value, column) {
                if (column?.formatFn && typeof column.formatFn === 'function') {
                    try {
                        return column.formatFn(value)
                    } catch (error) {
                        console.error('Failed to format value', error)
                        return value
                    }
                }
                return value
            },
            normalizeValue(value, column = {}) {
                if (column.type === 'number') {
                    const numeric = Number(
                        typeof value === 'string' ? value.replace(/[^0-9.-]+/g, '') : value
                    )
                    return isNaN(numeric) ? 0 : numeric
                }

                if (value instanceof Date) {
                    return value.getTime()
                }

                if (typeof value === 'string') {
                    const date = new Date(value)
                    if (!isNaN(date)) {
                        return date.getTime()
                    }
                    return value.toLowerCase()
                }

                return value ?? ''
            },
        },
    }
</script>

<style scoped lang="scss">
    table th,
    table td {
        text-align: center;
    }

    table tr:nth-child(even) {
        background-color: #bef2f5;
    }

    .table {
        width: 100%;
        thead {
            td,
            th {
                color: #ffffff;
                background-color: #000 !important;
            }
        }
    }

    .select-header {
        width: 54px;
    }

    .sortable-header {
        cursor: pointer;
    }

    .sortable-header__content {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        width: 100%;
    }

    .sort-indicator {
        display: inline-flex;
        flex-direction: column;
        gap: 2px;
        font-size: 0.75rem;
    }

    .table-actions {
        display: flex;
        justify-content: flex-end;
        margin-bottom: 0.5rem;
    }
</style>
